import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const InstructionalShiftsThatStick = () => {
  const post = getBlogPost('instructional-shifts-that-stick');
  return (
    <BlogArticle post={post}>
      <p>
        {
          'When Jen Lawson first introduced Curipod to her district, it wasn’t just about trying a new edtech tool. For the 25-year educator and Northville’s Instructional Technology Integration Coordinator, it was about supporting good teaching—making it easier to plan, deliver, and sustain lessons that spark connection and curiosity.'
        }
      </p>
      <p>
        {
          'Northville has used Curipod to bring more energy into classrooms. For teachers like Amy Soukup, it quickly became a go-to tool for driving engagement, building student confidence, and igniting her own excitement for teaching.'
        }
      </p>
      <h2>{'Engagement That Sticks'}</h2>
      <p>
        {
          'Amy Soukup, an English and Social Studies teacher at Meads Mill Middle School, didn’t begin using Curipod until May. But within weeks, her classroom looked—and sounded—very different.'
        }
      </p>
      <p>
        {
          '“They come in excited,” Amy shared. “They see Curipod on the screen and ask, ‘Are we doing Curipod?’ If I say no, I get a collective, ‘Aw man!’”'
        }
      </p>
      <p>
        {
          'The shift was especially noticeable in students who rarely participated—those who hadn’t responded to small group work, individual check-ins, or creative incentives. Now, they were raising their hands, typing thoughtful responses, and even competing for top-voted answers. One student, previously disengaged and quiet, now strives for the number one spot each day.'
        }
      </p>
      <p>
        {
          '“That was my answer,” a student beamed after seeing her response projected—something she never would have shared aloud before.'
        }
      </p>
      <p>
        {
          'For Amy, the energy didn’t feel like a one-off. It felt like sustained connection.'
        }
      </p>
      <blockquote>
        {
          '“A quiet classroom doesn’t necessarily mean it’s a learning classroom,” she said. “With Curipod, I’ve learned that louder sometimes means more learning.”'
        }
      </blockquote>
      <p>
        {
          'In Amy’s class, Curipod isn’t just an instructional tool. It’s a catalyst for student voice, creative expression, and social interaction. Students laughed, debated, drew, reflected, and moved—engaging in various learning modalities. She noticed a shift in the energy in the room—it changed who felt empowered to speak up and how they did so.'
        }
      </p>
      <h2>{'Lesson Planning for Real Classrooms'}</h2>
      <p>
        {
          'Curipod wasn’t developed in isolation. It’s shaped through ongoing collaboration with educators—something Amy and Jen noticed right away.'
        }
      </p>
      <p>
        {
          '“I just type in the standards and Curipod puts it all together. There’s still my thought and ideas behind it but without the exhausting process.”'
        }
      </p>
      <p>
        {
          'For Amy, planning lessons no longer meant spending hours trying to come up with new ways to keep students engaged. From vocabulary practice to test prep, Curipod helped her streamline the process—without sacrificing rigor or creativity.'
        }
      </p>
      <p>
        {
          'By removing some of the mental load, Curipod gave Amy more space to focus on what matters most: being present, energized, and connected with her students.'
        }
      </p>
      <blockquote>
        {
          '“It just puts it all together and adds some creativity to it. That takes away your exhaustion. Teaching is already mentally exhausting. This gives you more of your life back.”'
        }
      </blockquote>
      <h2>{'A New Standard for Engagement'}</h2>
      <p>
        {
          'What began as a short-term experiment in one classroom has grown into something larger for Northville. With tools that support real-time feedback, '
        }
        <Link href="/c/blog-posts/elevating-core-curriculum-with-curipod" passHref>
          <a>{'whole-class engagement'}</a>
        </Link>
        {
          ', and creative freedom, teachers like Amy are discovering a new way to approach instruction.'
        }
      </p>
      <p>
        {
          '“Curipod doesn’t just help you teach better,” she said. “It makes you feel better.”'
        }
      </p>
      <p>
        {
          'In Amy’s classroom, the change is visible—and audible. Laughter. Reflection. Debates. Applause. Students leaning in, not tuning out.'
        }
      </p>
      <p>
        {
          'This is the kind of change that happens when teachers are supported, students are heard, and classrooms come alive.'
        }
      </p>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default InstructionalShiftsThatStick;
